import type { PartnerAppointmentType, PartnerAvailableSlot } from "@cabocheck/shared";
import { addMinutes, format, isSameDay, max, startOfMonth } from "date-fns";
import { de, enUS } from "date-fns/locale";
import { useState } from "react";
import { DayPicker } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { buttonVariants } from "~/components/ui/button";
import { cn } from "~/utils";
import { formatDate, fromUnix, toUnix } from "~/utils/date";
import type { PartnerAvailableSpotsResponse } from "~/routes/api.v1.partner.appointments.available_spots";

export function CalendarView({ onSelect, type, partnerGuid }: Props) {
  const {
    i18n: { language },
  } = useTranslation();
  const [selectedDay, setSelectedDay] = useState<Date | undefined>(undefined);
  const [selectedMonth, setSelectedMonth] = useState<Date | undefined>(startOfMonth(new Date()));

  const _seventy_five_minutes_from_now = selectedMonth ? addMinutes(new Date(), 75) : null;
  const startTimestamp =
    _seventy_five_minutes_from_now && selectedMonth
      ? toUnix(max([_seventy_five_minutes_from_now, selectedMonth])).toString()
      : null;

  const { data: availableSpots = [] } = useQuery<PartnerAvailableSpotsResponse>({
    queryKey: ["partner_available_spots", partnerGuid, type?.partner_appointment_type_guid, startTimestamp],
    queryFn: async () => {
      if (!startTimestamp || !type?.partner_appointment_type_guid) return [];

      const searchParams = new URLSearchParams({
        partner_guid: partnerGuid,
        partner_appointment_type_guid: type.partner_appointment_type_guid,
        n_days: "30",
        start: startTimestamp,
      });

      const res = await fetch(`/api/v1/partner/appointments/available_spots?${searchParams.toString()}`);
      const json = await res.json();
      return json as PartnerAvailableSpotsResponse;
    },
    enabled: Boolean(type?.partner_appointment_type_guid && startTimestamp),
  });

  const isDisabled = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (date < today) {
      return true;
    }

    return !availableSpots.some((slot) => isSameDay(date, fromUnix(slot.start)));
  };

  return (
    <div className="flex h-full flex-col space-x-0 xl:flex-row xl:space-x-4 xl:divide-x">
      <div className="p-4 sm:p-6 md:p-8">
        <DayPicker
          onMonthChange={(date) => {
            setSelectedMonth(date);
          }}
          disabled={isDisabled}
          locale={language === "de" ? de : enUS}
          selected={selectedDay}
          onSelect={setSelectedDay}
          mode="single"
          classNames={{
            month: "space-y-4 sm:space-y-6 md:space-y-8",
            caption: "flex justify-between pt-1 relative items-center",
            caption_label: "font-bold text-sm sm:text-base",
            nav: "space-x-1 flex items-center",
            nav_button: cn("h-6 w-6 sm:h-7 sm:w-7 bg-transparent p-0 opacity-50 hover:opacity-100"),
            table: "w-full border-collapse space-y-1",
            head_row: "flex w-full space-x-1 sm:space-x-2",
            head_cell: "text-slate-500 rounded-md w-9 sm:w-12 md:w-16 font-normal text-[0.7rem] sm:text-[0.8rem]",
            row: "flex w-full mt-1 sm:mt-2",
            cell: "size-9 sm:size-12 md:size-16 text-center text-xs sm:text-sm m-0.5 sm:m-1 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-slate-100/50 [&:has([aria-selected])]:bg-slate-100 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
            day: cn(
              buttonVariants({ variant: "ghost" }),
              "size-9 sm:size-12 md:size-16 hover:bg-blue-550/30 text-sm sm:text-base md:text-lg cursor-pointer p-0 font-normal text-blue-550 font-bold rounded-lg bg-blue-550/10 aria-selected:opacity-100",
            ),
            day_today:
              "relative after:absolute after:bottom-1 sm:after:bottom-2 after:left-1/2 after:-translate-x-1/2 after:size-1 sm:after:size-1.5 after:rounded-full after:bg-blue-550",
            day_selected: "border-2 border-blue-550 !bg-blue-550/20 !text-black",
            day_outside:
              "day-outside text-slate-500 opacity-50  aria-selected:bg-slate-100/50 aria-selected:text-slate-500 aria-selected:opacity-30 ",
            day_disabled: "!bg-white !text-slate-500",
            day_range_middle: "aria-selected:bg-slate-100 aria-selected:text-slate-900 ",
            day_hidden: "invisible",
            day_range_start: "bg-slate-500 hover:text-white !font-medium",
            day_range_end: "day-range-end bg-slate-500 hover:text-white !font-medium",
          }}
        />
      </div>
      <div className="w-full">
        {selectedDay && (
          <div className="flex h-full flex-col space-y-4 p-4 sm:p-6 md:p-8 xl:rounded-r-[32px]">
            <p className="text-base text-gray-500 sm:text-lg">
              {format(selectedDay, "EEEE, dd.MM.yyyy", {
                locale: language === "de" ? de : enUS,
              })}
            </p>
            <div className="flex flex-col space-y-2 overflow-y-auto">
              {availableSpots
                .filter((s) => isSameDay(selectedDay, fromUnix(s.start)))
                .map((time) => {
                  const start = fromUnix(time.start);
                  const end = fromUnix(time.end);

                  return (
                    <button
                      onClick={() => {
                        onSelect(time);
                      }}
                      key={time.start}
                      className={"rounded-lg border border-blue-550 p-2 font-bold text-blue-550 hover:bg-blue-550/10"}
                    >
                      {formatDate(start, "time")} - {formatDate(end, "time", language)}
                    </button>
                  );
                })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface Props {
  onSelect: (slot: PartnerAvailableSlot) => void;
  type: PartnerAppointmentType;
  partnerGuid: string;
}
